import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { FirestoreService } from "../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../api/firebase/references";
import { CmmInputUniqueValuesTableDataModel } from "../data-model/tablesData";
import { CmmInputUniqueValuesTableColumnKeys } from "../enums/tableColumnKeys";
import {
  CmmInputUniqueValueItem,
  CmmInputUniqueValuesColumn,
  CmmInputUniqueValuesDoc,
  normalizeCmmInputUniqueValuesDoc,
  normalizeTopValues,
} from "../pages/configurations/pages/validationFunnel/pages/conditionList/cmmInputUniqueValuesTypes";

export type CmmInputUniqueValuesContextValue = {
  cmmInputUniqueValues: CmmInputUniqueValuesDoc | null;
  cmmInputUniqueValuesTableData: CmmInputUniqueValuesTableDataModel[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateShowOnList: (columnName: string, value: boolean) => void;
  saveTopValues: (
    columnName: string,
    newTopValues: CmmInputUniqueValueItem[],
  ) => Promise<void>;
};

function mapCmmInputColumnToTableRow(
  col: CmmInputUniqueValuesColumn,
): CmmInputUniqueValuesTableDataModel {
  const maxBadges = 8;
  const maxVisible = 2;
  const rawTopValues = normalizeTopValues(col.top_values);
  const badges = rawTopValues.slice(0, maxBadges).map((t) => {
    const isItemShape = t != null && typeof t === "object" && "value" in t;
    const value = isItemShape
      ? (t as { value: unknown; count?: number }).value
      : t;
    const count = isItemShape
      ? (t as { value: unknown; count?: number }).count
      : undefined;
    const valueStr = String(value ?? "").slice(0, 60);
    const suffix = String(value ?? "").length > 60 ? "…" : "";
    const countStr = count !== undefined ? ` (${count})` : "";
    return { text: `${valueStr}${suffix}${countStr}` };
  });
  return {
    [CmmInputUniqueValuesTableColumnKeys.COLUMN_NAME]: col.column_name,
    [CmmInputUniqueValuesTableColumnKeys.FIELD_NAME]: {
      data: col.display_name ?? "",
    },
    [CmmInputUniqueValuesTableColumnKeys.SHOW_ON_LIST]: {
      id: `cmm-show-on-list-${String(col.column_name)}`,
      defaultChecked: col.show_on_condition_list,
    },
    [CmmInputUniqueValuesTableColumnKeys.DATA_TYPE]: col.data_type,
    [CmmInputUniqueValuesTableColumnKeys.TOP_VALUES]: {
      badges,
      maxVisible,
      size: "small",
    },
  };
}

const CmmInputUniqueValuesContext =
  createContext<CmmInputUniqueValuesContextValue | null>(null);

type CmmInputUniqueValuesProviderProps = {
  children: ReactNode;
};

export const CmmInputUniqueValuesProvider = ({
  children,
}: CmmInputUniqueValuesProviderProps) => {
  const [cmmInputUniqueValues, setCmmInputUniqueValues] =
    useState<CmmInputUniqueValuesDoc | null>(null);
  const [cmmInputUniqueValuesTableData, setCmmInputUniqueValuesTableData] =
    useState<CmmInputUniqueValuesTableDataModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCmmInputUniqueValues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await FirestoreService.getDocument<CmmInputUniqueValuesDoc>(
        FirestoreDocumentReference.flaggingAlgorithmCmmInputUniqueValues(),
      );
      const normalized = normalizeCmmInputUniqueValuesDoc(data ?? null);
      setCmmInputUniqueValues(normalized);
      if (normalized?.columns?.length) {
        const tableData = normalized.columns.map(mapCmmInputColumnToTableRow);
        setCmmInputUniqueValuesTableData(tableData);
      } else {
        setCmmInputUniqueValuesTableData([]);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load CMM input unique values",
      );
      setCmmInputUniqueValues(null);
      setCmmInputUniqueValuesTableData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateShowOnList = useCallback((columnName: string, value: boolean) => {
    setCmmInputUniqueValuesTableData((prev) =>
      prev.map((row) => {
        const rowColumnName = row[
          CmmInputUniqueValuesTableColumnKeys.COLUMN_NAME
        ] as string;
        if (rowColumnName !== columnName) return row;
        const currentSwitch =
          row[CmmInputUniqueValuesTableColumnKeys.SHOW_ON_LIST];
        return {
          ...row,
          [CmmInputUniqueValuesTableColumnKeys.SHOW_ON_LIST]: {
            ...currentSwitch,
            defaultChecked: value,
          },
        };
      }),
    );
  }, []);

  const saveTopValues = useCallback(
    async (columnName: string, newTopValues: CmmInputUniqueValueItem[]) => {
      if (!cmmInputUniqueValues?.columns?.length) return;
      const updatedColumns = cmmInputUniqueValues.columns.map((col) =>
        col.column_name === columnName
          ? { ...col, top_values: newTopValues }
          : col,
      );
      await FirestoreService.updateDocument(
        "configurations/pharmacy_order_updates/metadata",
        "pa_requests",
        { columns: updatedColumns },
      );
      await fetchCmmInputUniqueValues();
    },
    [cmmInputUniqueValues, fetchCmmInputUniqueValues],
  );

  useEffect(() => {
    fetchCmmInputUniqueValues();
  }, [fetchCmmInputUniqueValues]);

  const value: CmmInputUniqueValuesContextValue = {
    cmmInputUniqueValues,
    cmmInputUniqueValuesTableData,
    loading,
    error,
    refetch: fetchCmmInputUniqueValues,
    updateShowOnList,
    saveTopValues,
  };

  return (
    <CmmInputUniqueValuesContext.Provider value={value}>
      {children}
    </CmmInputUniqueValuesContext.Provider>
  );
};

export const useCmmInputUniqueValuesContext =
  (): CmmInputUniqueValuesContextValue => {
    const context = useContext(CmmInputUniqueValuesContext);
    if (!context) {
      throw new Error(
        "useCmmInputUniqueValuesContext must be used within CmmInputUniqueValuesProvider",
      );
    }
    return context;
  };
